import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { titleize, unescapeHTML } from 'underscore.string';
import { debounce } from 'lodash';
import { Glyphicon, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { Translation } from 'react-i18next';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import { Label } from '@cargomatic/ui';
import CreateQuoteStore from 'stores/quotes/CreateQuoteStore';
import MarketsStore from 'stores/MarketsStore';
import PricingStore from 'stores/PricingStore';
import SessionStore from 'stores/SessionStore';
import ShipmentStore from 'stores/ShipmentStore';
import { sluggify } from 'utils/localization/utils';
import { DistanceUnitFormatter } from 'components/modules/master/formatters';
import LoadDetailsForm from 'components/load/LoadDetailsForm';
import {
  defaultLoad,
  DEFAULT_WEIGHT_UNIT,
  weightUnitsOptions,
} from 'utils/localization/types';
import { withLocaleContext } from 'providers/locale-settings-context';
import Checkbox from 'components/v2/forms/Checkbox';
import FSCDisclaimer from '../pricing/fsc-disclaimer';
import LoadingCircle from '../../ui/LoadingCircle';
import LocationAutosuggest from '../../site/location-autosuggest';
import Alert from '../../ui/Alert';
import { convertMetersToMiles } from '../../../utils/distance';
import { BOOKING_ACCESSORIALS } from '../../../config/accessorials';
import MarketActionCreators from '../../../actions/MarketActionCreators';
import ShipmentsActionCreators from '../../../actions/ShipmentsActionCreators';

const SHIPMENT_TYPES = ['SHORTHAUL'];
const BOOKING_ACCESSORIAL_KEYS = BOOKING_ACCESSORIALS.map((a) => a.value);

const accessorialsTooltip = (
  <Tooltip id="accessorial">
    <Translation>
      {(t, { i18n: _i18n }) => <>{t('APPLY_ADDITIONAL_FEATURES')}</>}
    </Translation>
  </Tooltip>
);

function getNewLoad(defaultLoadData) {
  return {
    ...defaultLoadData,
  };
}

const getInitialState = (accessorials, localeData) => {
  return {
    showFSCDisclaimer: false,
    editMode: false,
    market: 'LAX',
    marketError: null,
    bookingDisabled: false,
    accessorials,
    errors: {},
    shipment_type: 'shorthaul',
    fetchingDistance: false,
    distance: null,
    distance_in_miles: null,
    loads: [getNewLoad({ ...defaultLoad, ...localeData })],
    origin: {
      name: '',
      address: '',
      suite_or_apt: '',
      appointment_required: false,
    },
    destination: {
      name: '',
      address: '',
      suite_or_apt: '',
      appointment_required: false,
    },
    pricingState: {
      pending: false,
      complete: false,
      success: false,
      fail: false,
      error: null,
    },
    createQuoteState: {
      quote: null,
      pending: false,
      complete: false,
      success: false,
      fail: false,
      error: null,
    },
    canRequestPricing: false,
    full_truckload: false,
    price: null,
    weight: 0,
    weight_units: localeData.weight_units || DEFAULT_WEIGHT_UNIT,
  };
};

const mapExistingQuote = (quote, accessorials) => {
  const origin = quote.origin[0];
  const destination = quote.destination[0];

  let mappedAccessorials = { ...accessorials };
  if (quote.accessorials) {
    Object.keys(quote.accessorials).forEach((acc) => {
      mappedAccessorials[acc] = true;
    });
  }

  return {
    showFSCDisclaimer: false,
    _id: quote._id,
    editMode: true,
    market: quote.market,
    bookingDisabled: false,
    accessorials: { ...mappedAccessorials },
    errors: {},
    shipment_type: quote.shipment_type,
    fetchingDistance: false,
    distance: quote.distance,
    distance_in_miles: quote.distance_in_miles,
    loads: quote.loads,
    origin,
    destination,
    shipper: quote.shipper,
    payables: quote.payables,
    receivables: quote.receivables,
    pricingState: {
      pending: false,
      complete: false,
      success: false,
      fail: false,
      error: null,
    },
    createQuoteState: {
      quote: null,
      pending: false,
      complete: false,
      success: false,
      fail: false,
      error: null,
    },
    canRequestPricing: false,
    full_truckload: quote.full_truckload,
    price: quote.total_cost,
    weight: quote.weight,
    weight_units: quote.weight_units,
  };
};

class QuoteForm extends Component {
  static propTypes = {
    onCancel: PropTypes.func.isRequired,
    quote: PropTypes.object,
    shipper: PropTypes.shape({
      _id: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);

    const accessorials = BOOKING_ACCESSORIAL_KEYS.reduce(
      (runningMap, accessorial) => ({
        ...runningMap,
        [accessorial]: false,
      }),
      {},
    );

    const quote = props.quote;
    const localeData = props.localeData;
    this.requestQuote = debounce(this.requestQuote, 300);
    this.state = quote
      ? mapExistingQuote(quote, accessorials)
      : getInitialState(accessorials, localeData);
    this.requestQuote();
  }

  componentDidMount() {
    ShipmentStore.addChangeListener(this.handleDistanceChange);
    PricingStore.addChangeListener(this.handlePriceChange);
    CreateQuoteStore.addChangeListener(this.handleCreateQuoteChange);
    MarketsStore.addChangeListener(this.handleMarketChange);
  }

  componentWillUnmount() {
    ShipmentStore.removeChangeListener(this.handleDistanceChange);
    PricingStore.removeChangeListener(this.handlePriceChange);
    CreateQuoteStore.removeChangeListener(this.handleCreateQuoteChange);
    MarketsStore.removeChangeListener(this.handleMarketChange);
  }

  getDistance = async () => {
    const { destination, origin } = this.state;
    if (!destination.address || !origin.address) {
      return;
    }

    const { lng: originLng, lat: originLat } = origin;

    const { lng: destinationLng, lat: destinationLat } = destination;

    const {
      shipper: { _id: shipperId },
    } = this.props;

    const query = {
      originLat,
      originLng,
      destinationLat,
      destinationLng,
      shipperId,
    };

    this.setState({ fetchingDistance: true });
    const distance = await ShipmentsActionCreators.getDistanceV3(query);
    return distance;
  };

  getDistanceFormatted() {
    const { localeData } = this.props;
    const { distance } = this.state;

    if (distance === null) {
      return '-';
    }

    return (
      <DistanceUnitFormatter
        fromUnit="m"
        toUnit={localeData.distance_units}
        distance={distance}
      />
    );
  }

  // Transform the internal state into a cargomatic offer
  getPayload() {
    const {
      origin: currentOrigin,
      destination: currentDestination,
      accessorials: currentAccessorials,
      weight,
      weight_units,
      loads,
      distance,
      shipment_type,
      market,
      full_truckload,
      _id,
      payables,
      receivables,
      shipper,
    } = this.state;

    const origin = {
      ...currentOrigin,
    };

    const destination = {
      ...currentDestination,
    };

    const accessorials = Object.keys(currentAccessorials).reduce(
      (accessorialList, key) => {
        if (currentAccessorials[key]) {
          return accessorialList.concat({
            type: key,
            documents: [],
          });
        }
        return accessorialList;
      },
      [],
    );

    const shipperId = shipper?._id || this.props.shipper._id;

    const payload = {
      market,
      origin,
      destination,
      weight: weight ? parseInt(weight, 10) : 0,
      weight_units,
      accessorials,
      courier: false,
      shipper: {
        _id: shipperId,
      },
      full_truckload,
      distance,
      shipment_type,
      _id,
      payables,
      receivables,
    };

    const pallets = loads.filter((l) => l.type === 'pallets');
    const boxes = loads.filter((l) => l.type === 'boxes');

    if (pallets.length) {
      payload.pallets = pallets;
    }

    if (boxes.length) {
      payload.boxes = boxes;
    }

    return payload;
  }

  isLoadValid() {
    const { loads, full_truckload } = this.state;

    if (full_truckload) {
      return true;
    }

    if (loads.length === 0) {
      return false;
    }

    const { length, width, height, quantity } = loads[0];

    return length && width && height && quantity;
  }

  canRequestPricing() {
    const { origin, destination, weight, distance } = this.state;

    return (
      distance &&
      origin.address &&
      destination.address &&
      weight &&
      this.isLoadValid()
    );
  }

  canSave() {
    const {
      pricingState: { complete, success },
      fetchingDistance,
    } = this.state;
    return complete && success && !fetchingDistance;
  }

  requestQuote = () => {
    if (this.canRequestPricing()) {
      const payload = this.getPayload();
      ShipmentsActionCreators.updatePrice(payload);
    }
  };

  handleToggleFullTruckLoad = (full_truckload) => {
    const localeData = this.props.localeData;
    this.setState(() => {
      if (full_truckload) {
        return {
          full_truckload,
          loads: [],
        };
      }
      return {
        full_truckload: false,
        loads: [
          getNewLoad({
            ...defaultLoad,
            ...localeData,
          }),
        ],
      };
    }, this.requestQuote);
  };

  handleSuiteOrAptChange = (field) => (e) => {
    const {
      target: { value: suite_or_apt },
    } = e;

    this.setState((prevState) => {
      const location = {
        ...prevState[field],
        suite_or_apt,
      };

      return {
        [field]: location,
      };
    });
  };

  handleCreateQuoteChange = () => {
    const createQuoteState = CreateQuoteStore.getRequestState();
    const quote = CreateQuoteStore.getQuote();

    this.setState(
      {
        createQuoteState: {
          ...createQuoteState,
          quote,
        },
      },
      () => {
        if (createQuoteState.success) {
          setTimeout(() => this.props.onCancel(), 1000);
        }
      },
    );
  };

  handlePriceChange = () => {
    const requestState = PricingStore.getRequestState();

    this.setState({
      pricingState: {
        ...requestState,
      },
    });

    if (requestState.success) {
      const price = PricingStore.getPrice();
      if (price) {
        const showFSCDisclaimer = !!price.rerate_fsc;
        // cost includes mark up now
        this.setState({ price, showFSCDisclaimer });
      }
    }
  };

  handleSaveQuote = () => {
    const payload = this.getPayload();
    ShipmentsActionCreators.createQuote(payload);
  };

  handleUpdateQuote = () => {
    const payload = this.getPayload();
    ShipmentsActionCreators.updateQuote(payload);
  };

  handleDistanceChange = () => {
    const distance = ShipmentStore.getDistance();
    const distance_in_miles = convertMetersToMiles(distance);
    const fetchingDistance = false;

    this.setState(
      {
        distance,
        distance_in_miles,
        fetchingDistance,
      },
      this.requestQuote,
    );
  };

  handleRemoveLoad = (newLoads, _index) => {
    this.setState({ loads: newLoads });
  };

  handleLoadFieldChange = (newLoads, _field, _value, _index) => {
    this.setState({ loads: newLoads }, () => {
      this.requestQuote();
    });
  };

  handleAccessorialChange = (accessorialKey) => {
    this.setState((prevState) => {
      const { accessorials: currentAccessorials } = prevState;
      const accessorials = {
        ...currentAccessorials,
        [accessorialKey]: !currentAccessorials[accessorialKey],
      };

      return {
        accessorials,
      };
    }, this.requestQuote);
  };

  handleShipmentTypeChange = (e) =>
    this.setState({ shipment_type: e.target.value }, this.requestQuote);

  handleWeightUnitsChange = (item) => {
    this.setState({ weight_units: item.value });
  };

  handleLocationNameChange = (field) => (e) => {
    const {
      target: { value: name },
    } = e;
    this.setState((prevState) => {
      const location = {
        ...prevState[field],
        name,
      };

      return {
        [field]: location,
      };
    });
  };

  handleCheckboxChange = (field) => (appointment_required) => {
    this.setState((prevState) => {
      const location = {
        ...prevState[field],
        appointment_required,
      };

      return {
        [field]: location,
      };
    });
  };

  handleAddressSelect = (field) => (item) => {
    this.setState(
      (prevState) => {
        const { address, coords, lat, lng, place_id } = item;

        const location = {
          ...prevState[field],
          address,
          coords,
          lat,
          lng,
          place_id,
        };

        return {
          [field]: location,
        };
      },
      () => {
        this.getDistance();

        if (field === 'origin') {
          const {
            [field]: { lat, lng },
          } = this.state;
          MarketActionCreators.findMarket([lng, lat]);
        }
      },
    );
  };

  handleMarketChange = () => {
    const market = MarketsStore.getMarket();
    const marketError = MarketsStore.getMarketError();

    this.setState({ market: market ? market.code : null, marketError });
  };

  handleFieldChange = (field) => (e) =>
    this.setState({ [field]: e.target.value }, this.requestQuote);

  isSaveDisabled = () => {
    const {
      createQuoteState: { pending },
    } = this.state;

    return pending || !this.canSave();
  };

  fieldHasError(field) {
    return this.state.errors.hasOwnProperty(field);
  }

  renderAlerts() {
    const {
      createQuoteState: { complete, success, error, quote },
    } = this.state;

    if (!complete) {
      return null;
    }

    if (success) {
      const quoteLink = `/quotes/${quote._id}`;
      return (
        <Translation>
          {(t, { i18n: _i18n }) => (
            <Alert type="success" title={t('QUOTE_SAVED')}>
              {t('VIEW_QUOTE')}&nbsp;
              <a href={quoteLink}>
                <strong>{t('HERE')}</strong>
              </a>
            </Alert>
          )}
        </Translation>
      );
    }

    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <Alert type="error" title={error || t('ERROR_SAVING_QUOTE')} />
        )}
      </Translation>
    );
  }

  renderMarketAlert() {
    const { marketError } = this.state;
    if (!marketError) {
      return null;
    }
    return (
      <Translation>
        {(t, { i18n: _i18n }) => <Alert type="error" title={t(marketError)} />}
      </Translation>
    );
  }

  renderAccessorials() {
    const isShipper = SessionStore.isShipper();
    const isAdmin = SessionStore.isAdmin();
    const quoteAccessorials = BOOKING_ACCESSORIALS.filter((a) => {
      const key = a.value;

      if (key === 'white_glove' && !isAdmin) {
        return false;
      }

      if (a.shipper_can_book && isShipper) {
        return true;
      }
      if (a.include_in_booking && isAdmin) {
        return true;
      }
      return false;
    });
    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <div className="my-4">
            <div>
              <h3 className="inline-block text-xl mb-4">
                {t('ACCESSORIAL', { count: 2 })}
              </h3>
              <OverlayTrigger placement="top" overlay={accessorialsTooltip}>
                <Glyphicon glyph="info-sign" style={{ marginLeft: 10 }} />
              </OverlayTrigger>
            </div>
            <ul className="p-0 grid grid-cols-4">
              {quoteAccessorials.map((accessorial) => (
                <li key={accessorial.value} className="inline-block px-2 py-1">
                  <Checkbox
                    checked={this.state.accessorials[accessorial.value]}
                    label={t(accessorial.localizeDescription)}
                    onChange={() =>
                      this.handleAccessorialChange(accessorial.value)
                    }
                  >
                    {t(accessorial.localizeDescription)}
                  </Checkbox>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Translation>
    );
  }

  renderLocationField(field) {
    const fieldClassName = classnames({
      'px-4': true,
      'flex-1': true,
      error: this.fieldHasError(field),
    });

    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <div>
            <h3 className="text-xl mb-4">{titleize(t(sluggify(field)))}</h3>
            <div>
              <label className="form-label mb-2">{t('NAME')}</label>
              <input
                type="text"
                value={unescapeHTML(this.state[field].name)}
                className="form-field w-full mb-4"
                placeholder={t('LOCATION_NAME')}
                onChange={this.handleLocationNameChange(field)}
              />
            </div>
            <div className="flex flex-wrap -mx-4">
              <fieldset className={fieldClassName}>
                <label className="form-label mb-2 block flex">
                  <i className="fa fa-pin mr-1" />
                  <span>{t('ADDRESS')}</span>
                </label>
                <div className="mb-4 flex">
                  <LocationAutosuggest
                    value={this.state[field].address}
                    searchField="address"
                    callbackSelectedItem={this.handleAddressSelect(field)}
                    placeholderText={t('SEARCH_FOR_LOCATION')}
                  />
                </div>
              </fieldset>
              <fieldset className="px-4">
                <label className="form-label mb-2 block">
                  {t('SUITE_APT')}
                </label>
                <input
                  type="text"
                  className="form-field"
                  placeholder={t('SEARCH_SUITE')}
                  value={this.state[field].suite_or_apt}
                  onChange={this.handleSuiteOrAptChange(field)}
                />
              </fieldset>
            </div>
            <div className="flex items-center justify-start gap-2 mb-2 w-max">
              <Checkbox
                checked={this.state[field]?.appointment_required ?? false}
                id={`appointment_required_checkbox_quote_form`}
                onChange={this.handleCheckboxChange(field)}
              />
              <Label>{t('REQUIRES_APPOINTMENT')}</Label>
            </div>
          </div>
        )}
      </Translation>
    );
  }

  addLoad = (newLoads) => {
    this.setState({ loads: newLoads });
  };

  renderEditButton(pending) {
    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <button
            className="button button-green block w-full mr-2"
            onClick={this.handleUpdateQuote}
          >
            {!pending && <span>{t('UPDATE')}</span>}
            {pending && <LoadingCircle size={1} text={t('UPDATING')} />}
          </button>
        )}
      </Translation>
    );
  }

  renderRequiredAppointmentNote() {
    const isAppointmentRequired =
      this.state?.origin?.appointment_required ||
      this.state?.destination?.appointment_required;
    if (!isAppointmentRequired) return null;
    return (
      <Translation>
        {(t) => (
          <Alert
            dataTestId="Alert-RequiredAppointment-Quote"
            className="flex-1"
            type="warning"
            title={t('REQUIRED_APPOINTMENT_NOTES')}
          ></Alert>
        )}
      </Translation>
    );
  }

  render() {
    const {
      createQuoteState: { pending },
    } = this.state;

    return (
      <Translation>
        {(t, { i18n: _i18n }) => (
          <div className="relative">
            <div className="overflow-y-visible">
              {this.renderLocationField('origin')}
              {this.renderLocationField('destination')}
              <div className="flex flex-wrap -mx-4">
                <div className="px-4 md:w-1/3 w-full">
                  <label className="mb-2 form-label">
                    {t('SHIPMENT_TYPE')}
                  </label>
                  <select
                    id="quote-shipment-type-selector"
                    onChange={this.handleShipmentTypeChange}
                    value={this.state.shipment_type}
                    className="form-select mb-4 w-full"
                  >
                    {SHIPMENT_TYPES.map((shipmentType) => (
                      <option key={shipmentType} value={shipmentType}>
                        {titleize(t(shipmentType))}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="px-4 md:w-1/3 w-full">
                  <label className="form-label mb-2 block">
                    {titleize(t('WEIGHT'))}
                  </label>
                  <input
                    type="number"
                    className="form-field mb-4 w-full"
                    step="1"
                    placeholder={t('ENTER_WEIGHT')}
                    value={this.state.weight}
                    onChange={this.handleFieldChange('weight')}
                  />
                </div>
                <div className="px-4 md:w-1/3 w-full">
                  <CargomaticSelect
                    showCaption
                    label={t('WEIGHT_UNIT')}
                    selected={this.state.weight_units}
                    className="w-20"
                    items={weightUnitsOptions}
                    onSelect={this.handleWeightUnitsChange}
                    applyDefaultMargins={false}
                    valueKey="value"
                    textKey="localizationSlug"
                  />
                </div>
              </div>
              <div>
                <h3 className="inline-block text-xl">{t('LOAD_DETAILS')}</h3>
              </div>
              <CargomaticInput
                className="my-4"
                type="checkbox"
                id="full_truckload"
                label={t('FULL_TRUCKLOAD')}
                info={t('FULL_TRUCKLOAD_NOTE')}
                value={this.state.full_truckload}
                onChange={this.handleToggleFullTruckLoad}
              />
              <LoadDetailsForm
                loadOnly
                loads={this.state.loads}
                fullTruckload={this.state.full_truckload}
                addLoad={this.addLoad}
                onRemoveLoad={this.handleRemoveLoad}
                onLoadFieldChange={this.handleLoadFieldChange}
              />

              {this.renderAccessorials()}
            </div>
            <div className="sticky bottom-0 left-0">
              {this.renderMarketAlert()}
              {this.state.createQuoteState.complete && (
                <div className="bg-white p-4">{this.renderAlerts()}</div>
              )}
              <div className="bg-grey-lightest -mx-2 p-4">
                {this.state.showFSCDisclaimer && (
                  <div className="my-2">
                    <FSCDisclaimer />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="w-full">
                    {this.renderRequiredAppointmentNote()}
                  </div>
                  <div className="flex justify-end items-center w-full">
                    {this.state.distance && (
                      <div className="px-4">
                        <span className="text-xl font-bold">
                          {this.getDistanceFormatted()}
                        </span>
                      </div>
                    )}
                    <div className="px-4 actions flex">
                      {!this.state.editMode && (
                        <button
                          className="button button-green block w-full mr-2 quotes-quotesTable-saveNewQuote"
                          onClick={this.handleSaveQuote}
                          disabled={this.isSaveDisabled()}
                        >
                          {!pending && <span>{t('GET_QUOTE')}!</span>}
                          {pending && (
                            <LoadingCircle size={1} text={t('SAVING')} />
                          )}
                        </button>
                      )}
                      {this.state.editMode && this.renderEditButton(pending)}
                      <button
                        className="button button-secondary"
                        onClick={() => this.props.onCancel()}
                      >
                        {t('CANCEL')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Translation>
    );
  }
}

export default withLocaleContext(QuoteForm);
